'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["alt", "onLoad", "placeholderSrc", "imageHeight", "imageWidth", "borderRadius", "circle", "crop", "fluid"],
  _excluded2 = ["imageHeight", "imageWidth", "borderRadius", "circle", "crop", "fluid", "placeholderIcon"];
import React, { useRef, useState } from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import StyledImage, { Placeholder, PlaceholderImage as DefaultPlaceholderImage, PlaceholderImageWrapper, StyledLazyImage as DefaultStyledLazyImage } from "./Image.mjs";
import { useInView as defaultUseInView } from "./useInView.mjs";
export function defineLazyImage() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$StyledLazyImage = _ref.StyledLazyImage,
    StyledLazyImage = _ref$StyledLazyImage === void 0 ? DefaultStyledLazyImage : _ref$StyledLazyImage,
    _ref$PlaceholderImage = _ref.PlaceholderImage,
    PlaceholderImage = _ref$PlaceholderImage === void 0 ? DefaultPlaceholderImage : _ref$PlaceholderImage,
    _ref$useInView = _ref.useInView,
    useInView = _ref$useInView === void 0 ? defaultUseInView : _ref$useInView;
  return function LazyImage(_ref2) {
    var _ref2$alt = _ref2.alt,
      alt = _ref2$alt === void 0 ? '' : _ref2$alt,
      _onLoad = _ref2.onLoad,
      placeholderSrc = _ref2.placeholderSrc,
      imageHeight = _ref2.imageHeight,
      imageWidth = _ref2.imageWidth,
      borderRadius = _ref2.borderRadius,
      circle = _ref2.circle,
      crop = _ref2.crop,
      fluid = _ref2.fluid,
      props = _objectWithoutProperties(_ref2, _excluded);
    var imageRef = useRef(null);
    var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      inView = _useState2[0],
      setInView = _useState2[1];
    var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isLoaded = _useState4[0],
      setIsLoaded = _useState4[1];
    var lazyImage = /*#__PURE__*/React.createElement(StyledLazyImage, Object.assign({}, props, {
      alt: alt,
      ref: imageRef,
      onLoad: function onLoad(event) {
        setIsLoaded(true);
        if (_onLoad) {
          _onLoad(event);
        }
      },
      $isLoaded: isLoaded,
      $imageHeight: imageHeight,
      $imageWidth: imageWidth,
      $borderRadius: borderRadius,
      $circle: circle,
      $crop: crop,
      $fluid: fluid
    }));
    var placeholder = /*#__PURE__*/React.createElement(PlaceholderImageWrapper, Object.assign({
      $imageHeight: imageHeight,
      $imageWidth: imageWidth,
      $borderRadius: borderRadius,
      $circle: circle,
      $crop: crop,
      $fluid: fluid
    }, props), /*#__PURE__*/React.createElement(PlaceholderImage, {
      alt: alt,
      ref: imageRef,
      src: placeholderSrc
    }));
    useInView(imageRef, function () {
      setInView(true);
    });
    if (inView) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, !isLoaded && placeholder, lazyImage);
    }
    return placeholder;
  };
}
var LazyImage = defineLazyImage();

/**
 * Use Image to render a simple image by using a link to its location.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-image--docs) |
 * [Encore site](https://encore.spotify.net/web/components/image) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/Image) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=265-10&t=seYnsEY0fRrtuJhj-1)
 */
export var Image = function Image(_ref3) {
  var imageHeight = _ref3.imageHeight,
    imageWidth = _ref3.imageWidth,
    borderRadius = _ref3.borderRadius,
    circle = _ref3.circle,
    crop = _ref3.crop,
    fluid = _ref3.fluid,
    placeholderIcon = _ref3.placeholderIcon,
    props = _objectWithoutProperties(_ref3, _excluded2);
  if ('placeholderSrc' in props && props.placeholderSrc) {
    return /*#__PURE__*/React.createElement(LazyImage, Object.assign({
      imageHeight: imageHeight,
      imageWidth: imageWidth,
      borderRadius: borderRadius,
      circle: circle,
      crop: crop,
      fluid: fluid
    }, props, {
      "data-encore-id": ENCORE_DATA_IDS.Image
    }));
  } else if (placeholderIcon && !props.src) {
    var Icon = placeholderIcon;
    return /*#__PURE__*/React.createElement(Placeholder, Object.assign({
      $imageHeight: imageHeight,
      $imageWidth: imageWidth,
      $borderRadius: borderRadius,
      $circle: circle,
      $crop: crop,
      $fluid: fluid
    }, props, {
      "data-encore-id": ENCORE_DATA_IDS.Image
    }), /*#__PURE__*/React.createElement(Icon, null));
  }
  return /*#__PURE__*/React.createElement(StyledImage, Object.assign({
    $imageHeight: imageHeight,
    $imageWidth: imageWidth,
    $borderRadius: borderRadius,
    $circle: circle,
    $crop: crop,
    $fluid: fluid
  }, props, {
    "data-encore-id": ENCORE_DATA_IDS.Image
  }));
};